import Icon from '@components/Icon';
import { LeaderboardIcon } from '@components/Icons';
import { CloseIcon } from '@components/Icons/CloseIcon';
import { CompanionsIcon } from '@components/Icons/CompanionsIcon';
import { MagicWandIcon } from '@components/Icons/MagicWandIcon';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { isMacosPlatform } from '@utils/auth';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Features } from '../../config';

const NavIcon = memo(({ name, active }: { name: string; active: boolean }) => {
  const className = active
    ? '[&_svg]:fill-white'
    : '[&_svg]:fill-white opacity-[0.6] group-hover:[opacity:1] transition-all duration-300';
  return (
    <span className={className}>
      <Icon name={`${name}${active ? '-active' : ''}`} size={24} />
    </span>
  );
});

NavIcon.displayName = 'NavIcon';

export const Sidebar = memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen, setIsMinimized } = useCommonState();
  const { activePlaylist } = useMusicState();
  const activePage = location.pathname.split('/')[1] || 'home';
  const isStationMode = useStationMode();

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, [setSidebarOpen]);

  // Cookiebot is never loaded inside the native macOS shell (see index.html),
  // so the settings entry would be a dead button there.
  const showCookieSettings = !isMacosPlatform();

  const handleUpdateCookie = () => {
    window.Cookiebot?.renew();
  };

  const gotoPage = useCallback(() => {
    setSidebarOpen(false);
    if (activePlaylist) {
      setIsMinimized(true);
    }
  }, [setSidebarOpen, activePlaylist, setIsMinimized]);
  return (
    <>
      {/* Mobile overlay for X icon */}
      <div
        className={`fixed w-full h-full top-0 left-0 bg-black/80 z-998 transition-opacity duration-300 ease-in-out ${
          isStationMode ? '' : 'sm:display-none sm:hidden'
        } ${
          sidebarOpen
            ? 'opacity-100 pointer-events-all'
            : 'opacity-0 pointer-events-none'
        } `}
        onClick={closeSidebar}
      />
      <div
        id="sidebar"
        className={`flex absolute w-[280px] h-full top-0 left-0 font-[NapsterSans] transition-transform duration-300 ease-in-out z-[10001] ${
          isStationMode
            ? sidebarOpen
              ? 'translate-x-0'
              : '-translate-x-full'
            : `${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              } sm:-translate-x-0 sm:relative sm:w-[215px] sm:transition-none`
        } ${location.pathname === '/access-denied' ? 'hidden' : ''}`}>
        <div
          className={`w-full relative ${
            isStationMode ? 'bg-[#131313]' : 'bg-[#131313] sm:bg-white/5'
          }`}>
          {/* Header START */}
          <div className="flex relative h-[65px] items-center w-full px-4 sm:px-6">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center" onClick={gotoPage}>
                <img
                  src="/assets/images/logo_napster_white.svg"
                  alt="Napster"
                  className="h-8 w-auto z-[1001]"
                />
              </Link>
            </div>

            {/* Close button for mobile */}
            <div
              className={`flex absolute h-[64px] w-auto px-4 py-0 gap-[12px] justify-end right-[16px] items-center cursor-pointer ${
                isStationMode ? '' : 'sm:hidden'
              }`}
              onClick={closeSidebar}>
              <CloseIcon />
            </div>
          </div>
          {/* Header END */}

          {/* Main Menu wrapper START*/}
          <div className="flex px-4 sm:px-6 pb-8 sm:pb-30">
            <ul className="w-full flex flex-col cursor-pointer">
              <li
                className={`w-full py-3.5 hover:text-white ${
                  activePage === 'home' ? 'text-white' : 'text-white/60'
                } group text-[16px]`}>
                <Link
                  to="/"
                  className="w-full flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <NavIcon name="home" active={activePage === 'home'} />
                  {t('nav.home')}
                </Link>
              </li>
              <li
                className={`w-full py-3.5 hover:text-white ${
                  activePage === 'favorites' ? 'text-white' : 'text-white/60'
                } group text-[16px]`}>
                <Link
                  to="/favorites"
                  className="w-full flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <NavIcon name="heart" active={activePage === 'favorites'} />
                  {t('nav.favorites')}
                </Link>
              </li>
              <li
                className={`w-full py-3.5 hover:text-white ${
                  activePage === 'playlists' ? 'text-white' : 'text-white/60'
                } group text-[16px]`}>
                <Link
                  to="/playlists"
                  className="w-full flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <NavIcon
                    name="playlist"
                    active={activePage === 'playlists'}
                  />
                  {t('nav.myPlaylists')}
                </Link>
              </li>
              <li
                className={`w-full py-3.5 hover:text-white ${
                  activePage === 'my-creations' ? 'text-white' : 'text-white/60'
                } group text-[16px]`}>
                <Link
                  to="/my-creations"
                  className="w-full flex gap-3 items-center font-semibold leading-[150%] whitespace-nowrap"
                  onClick={gotoPage}>
                  <span
                    className={`flex-shrink-0 ${
                      activePage === 'my-creations'
                        ? 'opacity-100'
                        : 'opacity-60 group-hover:opacity-100 transition-opacity duration-300'
                    }`}>
                    <MagicWandIcon width={24} height={24} />
                  </span>
                  {t('nav.myCreations')}
                </Link>
              </li>
              {/* <li
                className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                  activePage === 'leaderboard' ? 'text-white' : 'text-white/60'
                }`}>
                <Link
                  to="/leaderboard"
                  className="flex items-center gap-3 w-full font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <span
                    className={
                      activePage === 'leaderboard'
                        ? 'text-white'
                        : 'text-white/60 group-hover:text-white transition-colors duration-300'
                    }>
                    <LeaderboardIcon width={24} height={24} />
                  </span>
                  <span className="text-left">Leaderboard</span>
                </Link>
              </li> */}
              <li
                className={`w-full py-3.5  group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                  activePage === 'musicforyou' ? 'text-white' : 'text-white/60'
                }`}>
                <Link
                  to="/musicforyou"
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <NavIcon name="track" active={activePage === 'musicforyou'} />
                  {t('nav.music')}
                </Link>
              </li>
              <li
                className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                  activePage === 'serenity-wellness'
                    ? 'text-white'
                    : 'text-white/60'
                }`}>
                <Link
                  to="/serenity-wellness"
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <span className="pt-[3px]">
                    <NavIcon
                      name="serenity"
                      active={activePage === 'serenity-wellness'}
                    />
                  </span>
                  {t('nav.serenity')}
                </Link>
              </li>
              <li
                className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                  activePage === 'audiobooks' ? 'text-white' : 'text-white/60'
                }`}>
                <Link
                  to="/audiobooks"
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <span className="pt-1">
                    <NavIcon
                      name="audiobooks"
                      active={activePage === 'audiobooks'}
                    />
                  </span>
                  {t('nav.audiobooks')}
                </Link>
              </li>
              <li
                className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                  activePage === 'companions' ? 'text-white' : 'text-white/60'
                }`}>
                <Link
                  to="/companions"
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <span
                    className={
                      activePage === 'companions'
                        ? 'text-white'
                        : 'text-white/60 group-hover:text-white transition-colors duration-300'
                    }>
                    <CompanionsIcon width={24} height={24} />
                  </span>
                  {t('nav.companions')}
                </Link>
              </li>
              {Features.enableIndianMusic && (
                <li
                  className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                    activePage === 'indian-music'
                      ? 'text-white'
                      : 'text-white/60'
                  }`}>
                  <Link
                    to="/indian-music"
                    className="flex gap-3 w-ful font-semibold leading-[150%]"
                    onClick={gotoPage}>
                    <NavIcon
                      name="track"
                      active={activePage === 'indian-music'}
                    />
                    {t('nav.indianMusic')}
                  </Link>
                </li>
              )}
              {Features.enableIndianMusic && Features.enableLeaderboard && (
                <li
                  className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                    activePage === 'indian-music-leaderboard'
                      ? 'text-white'
                      : 'text-white/60'
                  }`}>
                  <Link
                    to="/indian-music-leaderboard"
                    className="flex items-center gap-3 w-full font-semibold leading-[150%]"
                    onClick={gotoPage}>
                    <span
                      className={
                        activePage === 'indian-music-leaderboard'
                          ? 'text-white'
                          : 'text-white/60 group-hover:text-white transition-colors duration-300'
                      }>
                      <LeaderboardIcon width={24} height={24} />
                    </span>
                    <span className="text-left">{t('nav.leaderboard')}</span>
                  </Link>
                </li>
              )}

              {/* <li
                className={`w-full py-3.5 group hover:text-white transition-colors duration-300 ease-in-out text-[16px] ${
                  activePage === 'old-musics' ? 'text-white' : 'text-white/60'
                }`}>
                <Link
                  to="/old-musics"
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  onClick={gotoPage}>
                  <NavIcon name="track" active={activePage === 'old-musics'} />
                  Old Musics
                </Link>
              </li> */}
              {/* <li className="w-full py-3.5 text-white/60 group hover:text-white transition-colors duration-300 ease-in-out text-[16px]">
                <a
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  href="#">
                  <NavIcon name="podcast" active={false} />
                  Podcasts
                </a>
              </li>
              <li className="w-full py-3.5 text-white/60 group hover:text-white transition-colors duration-300 ease-in-out text-[16px]">
                <a
                  className="flex gap-3 w-ful font-semibold leading-[150%]"
                  href="#">
                  <NavIcon name="video" active={false} />
                  Video
                </a>
              </li> */}
            </ul>
          </div>
          {/* Main Menu wrapper END*/}

          {/* <Recent /> */}
        </div>
        <div className="fixed bottom-6 left-6 w-[183px] z-50 gap-2 hidden md:block text-start">
          <div className="flex flex-wrap gap-x-1">
            <Link
              to="https://www.napster.com/privacy-policy"
              className="text-white text-sm underline"
              target="_blank"
              rel="noopener noreferrer">
              {t('footer.privacy')}
            </Link>
            <span className="text-white text-sm">•</span>
            <Link
              to="https://www.napster.com/terms-of-service"
              className="text-white text-sm underline"
              target="_blank"
              rel="noopener noreferrer">
              {t('footer.terms')}
            </Link>
          </div>
          {showCookieSettings && (
            <button
              className="text-white text-sm underline flex-1"
              onClick={handleUpdateCookie}>
              {t('footer.cookieSettings')}
            </button>
          )}
        </div>
      </div>
    </>
  );
});

Sidebar.displayName = 'Sidebar';
